<template>
  <div :class="classList">
    <div class="st-tabs__list" role="tablist" :aria-label="ariaLabel">
      <button
        v-for="(tab, index) in tabs"
        :key="tab.type"
        @click="activateTab(index)"
        class="st-tabs__list__tab"
        :class="{ 'st-tabs__list__tab--is-active': activeTab === index }"
        role="tab"
        :aria-selected="activeTab === index"
        :aria-controls="`${tab.type}-tabb`"
        :id="`st-tab-${tab.type}`"
        tabindex="0"
        type="button"
      >
        <div
          class="st-tabs__list__tab__name"
          :class="{ 'st-tabs__list__tab__name--reversed': tab.iconReversed }"
        >
          <st-icon
            v-if="tab.iconName"
            :name="tab.iconName"
            :icon-props="tab.iconProps"
          />
          <span :data-text="capitalize(tab.name)">
            {{ capitalize(tab.name) }}
          </span>
        </div>
      </button>
    </div>
    <div
      class="st-tabs__content"
      role="tabpanel"
      :id="`${tabs[activeTab].type}-tabb`"
      :aria-labelledby="`st-tab-${tabs[activeTab].type}`"
    >
      <slot :name="tabs[activeTab].type"></slot>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { capitalize } from "../../helpers";
import StIcon from "../icon/Icon.vue";

const props = defineProps({
  tabs: Array,
  ariaLabel: String,
  font: {
    type: String,
    validator(font) {
      return ["primary"].includes(font);
    },
  },
});

const activeTab = ref(0);

function activateTab(index) {
  activeTab.value = index;
}

const classList = computed(() => {
  return ["st-tabs", props.font && `st-tabs--font-${props.font}`];
});
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-tabs {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  font-family: var(--font-secondary);
  color: var(--text-color-tabs);
  &--font-primary {
    font-family: var(--font-primary);
  }

  &__list {
    padding: 0;
    margin-bottom: rem-calc(16);
    display: grid;
    grid-template-columns: 1fr;
    gap: rem-calc(0) rem-calc(8);

    @media (min-width: 450px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @include medium {
      display: flex;
      flex-wrap: wrap;
      align-self: flex-start;
      border-bottom: var(--border-width--thin) solid
        var(--border-color-tertiary);
    }

    &__tab {
      background: none;
      border: none;
      border-bottom: var(--border-width--thin) solid
        var(--border-color-tertiary);
      border-top: var(--border-width--thin) solid transparent;
      outline: none;
      font-family: inherit;
      color: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: rem-calc(6) rem-calc(16);
      transition: 300ms linear background;
      cursor: pointer;

      @include medium {
        border-bottom: var(--border-width--thin) solid transparent;
      }

      &:hover {
        background: var(--background-color-quaternary);
      }

      &:focus {
        border: var(--border-width--thin) solid var(--border-color-quaternary);
      }

      &--is-active {
        font-weight: 600;
        border-bottom: var(--border-width) solid var(--border-color-active-tab);
        &:focus {
          border-bottom: var(--border-width) solid
            var(--border-color-active-tab);
        }
      }
      &__name {
        display: flex;
        flex-direction: row;
        align-items: center;
        font-size: rem-calc(14);
        height: rem-calc(24);
        line-height: rem-calc(24);
        white-space: nowrap;
        transition: 300ms linear border-bottom;
        gap: rem-calc(4);

        @include large {
          font-size: rem-calc(18);
        }

        &--reversed {
          flex-direction: row-reverse;
        }

        span::after {
          content: attr(data-text);
          font-weight: 600;
          visibility: hidden;
          height: 0;
          overflow: hidden;
          display: block;
        }
      }
    }
  }
}
</style>
